import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import ResultsSummary from '../components/results/ResultsSummary';
import ResultsTable from '../components/results/ResultsTable';
import ActionBar from '../components/results/ActionBar';
import ReportModal from '../components/results/ReportModal';
import { getResults, generateReport } from '../services/api';
import { generateReportWithProgress } from '../services/openrouter';

import { generatePDFReport } from '../services/pdfGenerator';
import { dbHelpers } from '../services/supabase';
import { matchIncentivesToProject, analyzeProjectContent, scoreIncentiveForProject } from '../services/incentiveMatching';

// Fallback report generation when OpenRouter API is not available
const generateFallbackReport = (userData, submissionData, incentivesData, onProgress) => {
  onProgress?.(25, 'Analyzing project content...');

  const project = submissionData?.parsed_metadata || {};
  const allIncentives = incentivesData || [];
  const budget = submissionData?.budget_amount || 1000000; // Default to $1M
  const budgetUSD = `$${(budget / 1000000).toFixed(1)}M USD`;

  onProgress?.(40, 'Scoring incentives against project requirements...');

  // Analyze project content and match to incentives
  const projectContent = {
    title: project.working_title || 'Untitled Project',
    genre: project.genre || [],
    productionType: project.production_type || 'feature',
    settingCountries: project.setting_countries || [],
    settingCities: project.setting_cities || [],
    period: project.period_years ? `${project.period_years.start}-${project.period_years.end}` : 'contemporary',
    language: project.primary_language || 'English',
    runtime: project.expected_runtime_minutes || 120,
    hasMinors: project.presence_of_minors || false,
    vfxIntensity: project.stunts_or_vfx_intensity || 'medium',
    localSpendPercent: project.likely_spend_split_local_percent || 60,
    budget: budget // CRITICAL: Pass the actual budget to matching algorithm
  };

  // Smart matching algorithm based on project characteristics
  const scoredIncentives = allIncentives.map(incentive => {
    let score = 0;
    let reasoning = [];

    try {
      // Genre matching
      if (projectContent.genre && projectContent.genre.length > 0) {
        const genreMatch = projectContent.genre.some(g => {
          if (typeof g === 'string' && typeof incentive.summary_one_liner === 'string') {
            return incentive.summary_one_liner.toLowerCase().includes(g.toLowerCase());
          }
          return false;
        });
        if (genreMatch) {
          score += 20;
          reasoning.push(`Strong genre alignment with ${projectContent.genre.join(', ')}`);
        }
      }

      // Location matching
      if (projectContent.settingCountries && projectContent.settingCountries.length > 0) {
        const locationMatch = projectContent.settingCountries.some(country => {
          if (typeof country === 'string' && typeof incentive.jurisdiction_name === 'string') {
            return incentive.jurisdiction_name.toLowerCase().includes(country.toLowerCase());
          }
          return false;
        });
        if (locationMatch) {
          score += 25;
          reasoning.push(`Location match with ${projectContent.settingCountries.join(', ')}`);
        }
      }

      // Budget compatibility - use accurate calculation based on database - FIXED
      const localSpendAmount = (budget * projectContent.localSpendPercent / 100);
      const ratePercent = Math.min(incentive.base_rate_percent || 0, 50); // Cap rate at 50%
      const estimatedSavings = Math.round(localSpendAmount * (ratePercent / 100));

      // Log for debugging accuracy
      console.log('📊 Fallback Savings Calculation:', {
        budget: `$${budget.toLocaleString()}`,
        localSpendPercent: projectContent.localSpendPercent,
        localSpendAmount: `$${localSpendAmount.toLocaleString()}`,
        rate: `${ratePercent}%`,
        estimatedSavings: `$${estimatedSavings.toLocaleString()}`
      });

      if (estimatedSavings > budget * 0.05) { // 5% of budget
        score += 15;
        reasoning.push(`High savings potential: $${estimatedSavings.toLocaleString()}`);
      }

      // Production type compatibility
      if (incentive.incentive_type === 'Tax Credit') {
        score += 10;
        reasoning.push('Tax credit structure aligns with production needs');
      }

      // Runtime consideration
      if (projectContent.runtime > 90 && typeof incentive.mark_stars === 'number' && incentive.mark_stars >= 4) {
        score += 10;
        reasoning.push('Reliable for feature-length productions');
      }

      // Special considerations
      if (projectContent.hasMinors) {
        const hasMinorSupport =
          (typeof incentive.tooltip?.rate === 'string' && incentive.tooltip.rate.toLowerCase().includes('minor')) ||
          (typeof incentive.tooltip?.cap === 'string' && incentive.tooltip.cap.toLowerCase().includes('minor')) ||
          (typeof incentive.tooltip?.min_spend === 'string' && incentive.tooltip.min_spend.toLowerCase().includes('minor')) ||
          (typeof incentive.summary_one_liner === 'string' && incentive.summary_one_liner.toLowerCase().includes('minor'));

        if (hasMinorSupport) {
          score += 5;
          reasoning.push('Considers child actor requirements');
        }
      }

      if (projectContent.vfxIntensity === 'high') {
        const hasVFXSupport =
          (typeof incentive.tooltip?.rate === 'string' && incentive.tooltip.rate.toLowerCase().includes('vfx')) ||
          (typeof incentive.tooltip?.cap === 'string' && incentive.tooltip.cap.toLowerCase().includes('vfx')) ||
          (typeof incentive.tooltip?.min_spend === 'string' && incentive.tooltip.min_spend.toLowerCase().includes('vfx')) ||
          (typeof incentive.summary_one_liner === 'string' && (
            incentive.summary_one_liner.toLowerCase().includes('vfx') ||
            incentive.summary_one_liner.toLowerCase().includes('visual') ||
            incentive.summary_one_liner.toLowerCase().includes('effects')
          ));

        if (hasVFXSupport) {
          score += 5;
          reasoning.push('Supports high VFX requirements');
        }
      }
    } catch (error) {
      console.warn('Error scoring incentive:', incentive.jurisdiction_name, error);
      // Continue with basic score if there's an error
    }

    // Calculate estimated spend for this incentive - FIXED
    const localSpendAmount = (budget * projectContent.localSpendPercent / 100);
    const rate = Math.min(incentive.base_rate_percent || 0, 50); // Cap rate at 50%
    const qualifiedSpend = Math.min(localSpendAmount, incentive.tooltip?.cap ? parseFloat(incentive.tooltip.cap.replace(/[^0-9.]/g, '')) || localSpendAmount : localSpendAmount);
    const calculatedEstimatedSavings = Math.round(qualifiedSpend * (rate / 100));

    return {
      ...incentive,
      matchScore: Math.max(0, Math.min(100, score)), // Ensure score is between 0-100
      matchReasoning: reasoning,
      estimatedSavings: calculatedEstimatedSavings
    };
  });

  // Sort by match score and get top matches
  const topIncentives = scoredIncentives
    .sort((a, b) => b.matchScore - a.matchScore)
    .slice(0, 5);

  const localSpendAmount = (budget * projectContent.localSpendPercent / 100);
  const topSavings = topIncentives[0]?.estimatedSavings || 0;
  const roiPercent = ((topSavings / budget) * 100).toFixed(1);

  onProgress?.(70, 'Generating project-specific recommendations...');

  let reportContent = `# PROFESSIONAL INCENTIVE ANALYSIS REPORT

## EXECUTIVE SUMMARY

**Client:** ${userData.fullName}
**Project:** ${project.working_title || 'Untitled Feature Film'}
**Budget:** ${budgetUSD}
**Analysis Date:** ${new Date().toLocaleDateString()}

### Key Findings:
- **Top Match:** ${topIncentives[0]?.jurisdiction_name || 'N/A'} - Match Score: ${topIncentives[0]?.matchScore || 0}/100
- **Potential Savings:** $${topIncentives[0]?.estimatedSavings?.toLocaleString() || '0'} USD (${roiPercent}% of budget)
- **Total Opportunities:** ${topIncentives.length} matched incentives across global locations
- **Local Spend Required:** ${projectContent.localSpendPercent}% ($${localSpendAmount.toLocaleString()} USD)

### Why it's a Top Choice:
${topIncentives[0]?.jurisdiction_name || 'The recommended location'} offers the best combination of incentive generosity, production infrastructure, and alignment with your ${projectContent.genre.join('/')} project requirements. With a ${topIncentives[0]?.base_rate_percent}% incentive rate and strong support for ${projectContent.productionType} films, it provides an excellent foundation for maximizing your production value.

### Project Fit:
Your ${projectContent.genre.join('/')} ${projectContent.productionType} film with a ${projectContent.runtime}-minute runtime and ${projectContent.localSpendPercent}% local spend requirement aligns perfectly with ${topIncentives[0]?.jurisdiction_name}'s incentive program. The location's focus on ${projectContent.vfxIntensity === 'high' ? 'high-end VFX productions' : 'quality feature films'} makes it an ideal choice for your project scope and technical needs.

### Challenge:
One potential challenge for your production is meeting the ${projectContent.localSpendPercent}% local spend requirement while maintaining production quality. The recommended incentives help overcome this by providing generous rebates that effectively reduce your net local spend obligations.

### AI-Powered Project Analysis:
After analyzing your "${projectContent.title}" project, I've identified the most suitable incentive programs based on your specific production characteristics:

**Project Profile:**
- **Genre:** ${projectContent.genre.join(', ') || 'Not specified'}
- **Production Type:** ${projectContent.productionType} (${projectContent.runtime} minutes)
- **Setting:** ${projectContent.settingCountries.join(', ') || 'Global/TBD'}
- **Language:** ${projectContent.language}
- **Technical Requirements:** ${projectContent.vfxIntensity === 'high' ? 'High VFX/stunts' : projectContent.vfxIntensity === 'medium' ? 'Moderate technical needs' : 'Standard production'}
- **Special Considerations:** ${projectContent.hasMinors ? 'Includes child actors' : 'Standard cast requirements'}

**Top Recommendation - ${topIncentives[0]?.jurisdiction_name}:**
This location achieves the highest compatibility score (${topIncentives[0]?.matchScore}/100) for your project due to:
${topIncentives[0]?.matchReasoning?.map(reason => `- ${reason}`).join('\n') || '- Strong overall alignment with your production requirements'}

## PROJECT ANALYSIS

### Production Details
- **Production Type:** ${project.production_type || 'Feature Film'}
- **Genre:** ${project.genre?.join(', ') || 'N/A'}
- **Setting:** ${project.setting_countries?.join(', ') || 'N/A'}
- **Language:** ${project.primary_language || 'English'}
- **Runtime:** ${project.expected_runtime_minutes || 120} minutes
- **Local Spend Allocation:** ${project.likely_spend_split_local_percent || 60}%

### Technical Considerations
- **Presence of Minors:** ${project.presence_of_minors ? 'Yes' : 'No'}
- **Stunts/VFX Intensity:** ${project.stunts_or_vfx_intensity || 'Medium'}
- **Period Setting:** ${project.period_years ? `${project.period_years.start} - ${project.period_years.end}` : 'Contemporary'}

## TOP INCENTIVE RECOMMENDATIONS
`;

  onProgress?.(70, 'Compiling recommendations...');

  topIncentives.forEach((incentive, index) => {
    const savings = incentive.estimatedSavings || 0;
    const savingsPercent = ((savings / budget) * 100).toFixed(1);
    const matchScore = incentive.matchScore || 0;

    reportContent += `
### ${index + 1}. ${incentive.jurisdiction_name} - ${incentive.incentive_name}
**Match Score:** ${matchScore}/100 | **Type:** ${incentive.incentive_type}
**Base Rate:** ${incentive.base_rate_percent}% | **Estimated Savings:** $${savings.toLocaleString()} USD (${savingsPercent}% of budget)
**Reliability Rating:** ${incentive.mark_stars}/5 stars

**Why This Matches Your Project:**
${incentive.matchReasoning?.map(reason => `- ${reason}`).join('\n') || '- Strong alignment with your production requirements'}

**Program Summary:** ${incentive.summary_one_liner}

**Key Eligibility Requirements:**
- ${incentive.tooltip?.rate || 'Rate details available from commission'}
- ${incentive.tooltip?.min_spend || 'Minimum spend requirements apply'}
- ${incentive.tooltip?.cap || 'Spending caps may apply'}

**Official Website:** ${incentive.official_url}
`;
  });

  reportContent += `

## FINANCIAL IMPACT ANALYSIS

### Projected Savings Breakdown
`;

  topIncentives.forEach((incentive, index) => {
    const savings = incentive.estimatedSavings || 0;
    const rate = incentive.base_rate_percent;
    const localSpend = (budget * projectContent.localSpendPercent / 100);
    const qualifiedSpend = Math.min(localSpend, incentive.tooltip?.cap ? parseFloat(incentive.tooltip.cap.replace(/[^0-9.]/g, '')) : localSpend);

    reportContent += `
**${incentive.jurisdiction_name} (Match Score: ${incentive.matchScore}/100):**
- Qualified Local Spend: $${qualifiedSpend.toLocaleString()} USD (${projectContent.localSpendPercent}% of budget)
- Incentive Rate: ${rate}%
- Estimated Rebate: $${savings.toLocaleString()} USD
- Net Production Cost After Incentives: $${(budget - savings).toLocaleString()} USD
- ROI: ${((savings / budget) * 100).toFixed(1)}% cost reduction
`;
  });

  reportContent += `

## STRATEGIC RECOMMENDATIONS

### 1. Primary Recommendation - ${topIncentives[0]?.jurisdiction_name || 'Top-ranked incentive'}
**Match Score: ${topIncentives[0]?.matchScore || 0}/100**

**Why this is perfect for "${projectContent.title}":**
Based on your project's specific characteristics, ${topIncentives[0]?.jurisdiction_name} achieves the highest compatibility score due to:

${topIncentives[0]?.matchReasoning?.map(reason => `- ${reason}`).join('\n') || '- Exceptional alignment with your production requirements'}

**Project-Specific Benefits:**
- **Genre Alignment:** Perfect for ${projectContent.genre.join(' and ')} productions like yours
- **Technical Support:** ${projectContent.vfxIntensity === 'high' ? 'Strong VFX infrastructure and expertise' : 'Solid production support network'}
- **Location Advantages:** ${projectContent.settingCountries.length > 1 ? 'Multi-location production support' : 'Streamlined single-location workflow'}
- **Language & Cultural Fit:** ${projectContent.language !== 'English' ? 'International production expertise' : 'English-language production stronghold'}

**Financial Impact for Your Budget:**
- **Total Savings:** $${topIncentives[0]?.estimatedSavings?.toLocaleString() || topSavings.toLocaleString()} USD
- **ROI:** ${((topIncentives[0]?.estimatedSavings || topSavings) / budget * 100).toFixed(1)}% reduction in production costs
- **Cash Flow:** Funds can be redirected to ${projectContent.vfxIntensity === 'high' ? 'enhanced VFX capabilities' : 'improved post-production quality'}

**Implementation Timeline:**
- **Week 1-2:** Initial consultation with ${topIncentives[0]?.jurisdiction_name} Film Commission
- **Month 1:** Complete application package with project details
- **Month 2:** Begin pre-production with incentive planning
- **Month 3-6:** Production execution with monitored spending
- **Month 6-9:** Rebate reconciliation and final payments

### 2. Contingency Planning
**Backup Options:**
Should ${topIncentives[0]?.jurisdiction_name} prove unavailable due to scheduling conflicts or location changes, consider these alternatives:

`;

  topIncentives.slice(1, 3).forEach((incentive, index) => {
    const backupSavings = incentive.estimated_savings || 0;
    const backupRoi = ((backupSavings / budget) * 100).toFixed(1);
    reportContent += `- **${incentive.jurisdiction_name}:** ${incentive.base_rate_percent}% rebate ($${incentive.estimatedSavings?.toLocaleString() || backupSavings.toLocaleString()} USD potential savings, ${((incentive.estimatedSavings || backupSavings) / budget * 100).toFixed(1)}% ROI)
  - Well-suited for ${project.genre?.[0] || 'your genre'} productions
  - ${incentive.jurisdiction_name} offers ${incentive.mark_stars}/5 stars reliability rating
  - Consider if your ${project.expected_runtime_minutes || 120}-minute runtime fits their sweet spot\n`;
  });

  reportContent += `

### 3. Project-Specific Optimization Strategies

**For your "${project.working_title || 'Untitled Project'}" production:**

#### Budget Allocation Strategy:
- **Prioritize Local Spend:** Allocate $${(budget * (project.likely_spend_split_local_percent || 60) / 100).toLocaleString()} USD (${project.likely_spend_split_local_percent || 60}%) to ${topIncentives[0]?.jurisdiction_name} for maximum rebate eligibility
- **Phase Spending:** Structure your ${project.expected_runtime_minutes || 120}-week production to align with incentive payment milestones
- **Contingency Buffer:** Maintain 10% of your $${budgetUSD} budget as a contingency for unexpected incentive-related costs

#### Production Planning Considerations:
- **Location Scouting:** ${project.setting_countries?.length > 1 ? 'Your multi-location approach works well with incentive stacking opportunities' : 'Focus on maximizing local production values in your primary location'}
- **Crew & Talent:** ${project.primary_language !== 'English' ? 'Consider local crew incentives for international co-productions' : 'Leverage local talent incentives to enhance production quality'}
- **Post-Production:** Use incentive savings to enhance ${project.stunts_or_vfx_intensity === 'high' ? 'VFX and post-production' : 'sound and color grading'}

#### Risk Management:
- **Timeline Buffer:** Build 2-3 week contingency for incentive application processing
- **Documentation:** Maintain detailed expense tracking from day one of pre-production
- **Legal Review:** Have incentive agreements reviewed by production counsel

## NEXT STEPS - DETAILED TIMELINE

### Week 1-2: Foundation Building
**Priority Actions:**
1. **Budget Breakdown for Top 3 Locations:**
   - **${topIncentives[0]?.jurisdiction_name}:** $${Math.round(budget * 0.4).toLocaleString()} USD (40% of budget) - Primary filming location
   - **${topIncentives[1]?.jurisdiction_name}:** $${Math.round(budget * 0.35).toLocaleString()} USD (35% of budget) - Secondary option
   - **${topIncentives[2]?.jurisdiction_name}:** $${Math.round(budget * 0.25).toLocaleString()} USD (25% of budget) - Backup location

2. **Schedule Consultation Calls:** Contact film commissions within 48 hours
   - **${topIncentives[0]?.jurisdiction_name} Film Commission:** Discuss ${topIncentives[0]?.base_rate_percent}% incentive program
   - **${topIncentives[1]?.jurisdiction_name} Film Commission:** Compare ${topIncentives[1]?.base_rate_percent}% vs ${topIncentives[0]?.base_rate_percent}% rates
   - Prepare your ${project.genre?.join('/')} project synopsis for discussions

3. **Documentation Assembly:** Begin collecting required materials
   - Business registration and producer credentials
   - Detailed production budget breakdown showing local spend allocation
   - Shooting schedule and location plans
   - Cast and crew employment agreements

### Week 3: Detailed Research
**Location-Specific Research:**
1. **${topIncentives[0]?.jurisdiction_name}:** [${topIncentives[0]?.official_url || '#'}] (${topIncentives[0]?.jurisdiction_name} Film Commission)
   - Research ${topIncentives[0]?.base_rate_percent}% incentive application process
   - Identify local crew and vendor contacts
   - Review post-production facility availability

2. **${topIncentives[1]?.jurisdiction_name}:** [${topIncentives[1]?.official_url || '#'}] (${topIncentives[1]?.jurisdiction_name} Film Commission)
   - Compare infrastructure and production support
   - Evaluate crew availability and expertise
   - Assess transportation and logistics requirements

3. **${topIncentives[2]?.jurisdiction_name}:** [${topIncentives[2]?.official_url || '#'}] (${topIncentives[2]?.jurisdiction_name} Film Commission)
   - Backup location analysis and requirements
   - Cost comparison and risk assessment

### Week 4: Planning & Preparation
**Production Planning:**
1. **Location Scouting:** Coordinate site visits for top locations
2. **Crew & Vendor Identification:** Local talent contracts and agreements
3. **Legal & Financial Planning:** Incentive specialist consultation
4. **Budget Refinement:** Adjust allocations based on research findings

### Month 1: Application & Planning
**Critical Milestones:**
1. **Formal Application Submission:** Complete and submit incentive application
   - Include detailed budget showing $${localSpendAmount.toLocaleString()} USD local spend
   - Provide production timeline aligned with incentive deadlines
   - Submit creative materials showcasing your project's ${project.primary_language !== 'English' ? 'international appeal' : 'cultural significance'}

2. **Production Planning Integration:** Incorporate incentive requirements into production planning
   - Adjust location schedule to maximize eligible spending
   - Plan crew and vendor contracts to meet local spend requirements
   - Coordinate with local production service providers

3. **Financial Modeling:** Refine budget projections
   - Model cash flow with incentive payments
   - Plan for contingency scenarios
   - Identify reinvestment opportunities for incentive savings

### Month 2-3: Production Execution
**Operational Excellence:**
1. **Spending Compliance:** Monitor and document eligible expenditures
   - Track local vs. non-local spending daily
   - Maintain detailed receipts and invoices
   - Prepare interim reporting documentation

2. **Quality Enhancement:** Leverage incentive savings for production upgrades
   - ${project.stunts_or_vfx_intensity === 'high' ? 'Enhanced VFX capabilities' : 'Additional shooting days'}
   - ${project.primary_language !== 'English' ? 'Professional translation services' : 'Superior post-production facilities'}
   - ${project.presence_of_minors ? 'Enhanced child actor protections and coaching' : 'Additional production support'}

3. **Risk Monitoring:** Regular compliance check-ins
   - Weekly spending reviews against targets
   - Documentation audits and gap analysis
   - Contingency planning for any deviations

### Month 4-6: Reconciliation & Optimization
**Final Phase:**
1. **Incentive Reconciliation:** Prepare final expenditure reports
   - Compile comprehensive spending documentation
   - Calculate final rebate amounts
   - Submit reconciliation package

2. **Performance Analysis:** Evaluate incentive program effectiveness
   - Compare projected vs. actual savings
   - Document lessons learned for future productions
   - Assess ROI and process efficiency

3. **Future Planning:** Apply insights to upcoming projects
   - Identify additional incentive opportunities
   - Build relationships with film commissions
   - Optimize processes for future applications

## POTENTIAL CHALLENGES

### Cultural Tests (${topIncentives[0]?.jurisdiction_name}/${topIncentives[1]?.jurisdiction_name}):
For your ${project.genre?.join('/')} project, ensure content meets cultural requirements. ${topIncentives[0]?.jurisdiction_name} may require specific cultural elements, while ${topIncentives[1]?.jurisdiction_name} focuses on local content representation. Plan for cultural consultants and script adjustments to meet these requirements.

### Tax Credit Monetization (${topIncentives[1]?.jurisdiction_name}/${topIncentives[0]?.jurisdiction_name}):
Tax credits are typically claimed post-production. For your $${budgetUSD} budget, this means managing cash flow for 6-12 months after completion. Consider working with a tax credit specialist to optimize timing and ensure proper documentation for maximum benefit realization.

### Currency Fluctuation:
With your $${budgetUSD} budget, currency fluctuations could impact final savings. Monitor exchange rates between ${project.primary_language === 'English' ? 'USD and local currencies' : 'USD, EUR, and local currencies'}. Consider hedging strategies or timing payments to minimize currency risk.

### Distance from ${project.setting_countries?.[0] || 'US'}:
International production adds complexity. Plan for travel, communication, and coordination across time zones. Factor in shipping costs for equipment and additional insurance requirements for international shoots.

## CONCLUSION

Based on your ${budgetUSD} ${projectContent.productionType} "${projectContent.title}" with ${projectContent.genre.join('/')} elements set in ${projectContent.settingCountries.join(', ') || 'multiple locations'}, our AI-powered analysis has identified ${topIncentives[0]?.jurisdiction_name} as your optimal location with a match score of ${topIncentives[0]?.matchScore || 0}/100.

**Key Advantages for Your Project:**
- **Perfect Genre Fit:** ${projectContent.genre.join(' and ')} productions thrive in ${topIncentives[0]?.jurisdiction_name}
- **Technical Alignment:** ${projectContent.vfxIntensity === 'high' ? 'Advanced VFX infrastructure matches your needs' : 'Strong production support network available'}
- **Language & Cultural Match:** ${projectContent.language !== 'English' ? 'International production expertise' : 'Native English production environment'}
- **Timeline Compatibility:** ${projectContent.runtime}-minute runtime fits their incentive sweet spot

**Strategic Implementation Plan:**
1. **Immediate Action:** Contact ${topIncentives[0]?.jurisdiction_name} Film Commission within the next 48 hours
2. **Week 1-2:** Complete application package with project-specific details
3. **Month 1:** Begin pre-production with incentive-qualified spending
4. **Ongoing:** Monitor local spend allocation (${projectContent.localSpendPercent}% target)
5. **Post-Production:** Efficient reconciliation and rebate collection

**Expected ROI:** ${roiPercent}% cost reduction through intelligent incentive matching and strategic implementation.

---

*This report was generated by NuPitch Incentive Match Maker AI. Always verify details directly with incentive agencies as requirements may change.*
`;

  onProgress?.(100, 'Report generated successfully!');
  return reportContent;
};

const Results = ({ isShared = false }) => {
  const { submissionId, shareToken } = useParams();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [sortBy, setSortBy] = useState('rank');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const data = await getResults(isShared ? shareToken : submissionId, isShared);

        setResults(data);
      } catch (err) {
        setError(err.message);
        toast.error('Failed to load results');
      } finally {
        setLoading(false);
      }
    };

    fetchResults();

    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, [submissionId, shareToken, isShared]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleGenerateReport = async (userData, submissionData, incentivesData, onProgress) => {
    try {
      // Validate required data
      if (!userData?.fullName || !userData?.email) {
        throw new Error('User name and email are required to generate the report.');
      }

      if (!submissionData || !incentivesData) {
        throw new Error('Project data is missing. Please try uploading your script again.');
      }

      onProgress?.(5, 'Analyzing your project details...');
      await new Promise(resolve => setTimeout(resolve, 800));
      
      onProgress?.(12, 'Evaluating global incentive database...');
      await new Promise(resolve => setTimeout(resolve, 600));

      // Get OpenRouter API key from environment
      const openRouterApiKey = import.meta.env.VITE_OPENROUTER_API_KEY;
      let reportContent;

      console.log('🔑 OpenRouter API Key Status:', openRouterApiKey ? '✅ Configured' : '❌ Not configured');

            if (!openRouterApiKey) {
        console.warn('⚠️  OpenRouter API key not configured. Using intelligent fallback with database data.');
        console.log('📊 Using database incentives data for fallback report:', incentivesData?.length, 'incentives');
        onProgress?.(18, 'Generating analysis with database data...');
        await new Promise(resolve => setTimeout(resolve, 700));

        // Use fallback report generation with actual database data
        reportContent = generateFallbackReport(userData, submissionData, incentivesData, onProgress);
      } else {
        // Generate report content using OpenRouter
        onProgress?.(18, 'Connecting to AI analysis engine...');
        await new Promise(resolve => setTimeout(resolve, 700));
        reportContent = await generateReportWithProgress(
          userData,
          submissionData,
          incentivesData,
          openRouterApiKey,
          onProgress
        );
      }

      // Generate PDF with progress updates
      onProgress?.(82, 'Optimizing financial projections...');
      await new Promise(resolve => setTimeout(resolve, 600));

      onProgress?.(88, 'Creating professional PDF report...');
      await new Promise(resolve => setTimeout(resolve, 500));

      const pdfBlob = await generatePDFReport(reportContent, userData, submissionData, incentivesData);

      // Keep progress bar moving during finalization
      onProgress?.(92, 'Finalizing recommendations...');
      await new Promise(resolve => setTimeout(resolve, 400));
      onProgress?.(94, 'Adding final touches...');
      await new Promise(resolve => setTimeout(resolve, 400));
      onProgress?.(96, 'Preparing download...');

      // Save report request to database
      try {
        const startTime = Date.now();
        const reportRequestData = {
          submission_id: submissionData.id,
          user_name: userData.fullName,
          user_email: userData.email,
          project_title: submissionData?.parsed_metadata?.working_title || 'Untitled Project',
          project_genre: submissionData?.parsed_metadata?.genre?.join(', ') || 'Unknown',
          project_budget: submissionData?.budget_amount || null,
          project_location: submissionData?.parsed_metadata?.setting_countries?.join(', ') || 'Unknown',
          report_content: reportContent,
          pdf_filename: `nupitch-incentive-report-${new Date().toISOString().split('T')[0]}.pdf`,
          terms_accepted: userData.termsAccepted,
          newsletter_opted_in: userData.marketingOptIn,
          terms_accepted_at: new Date().toISOString(),
          generation_method: import.meta.env.VITE_OPENROUTER_API_KEY ? 'openrouter' : 'fallback',
          ai_model_used: 'nupitch-intelligent-matching',
          processing_time_seconds: Math.round((Date.now() - startTime) / 1000)
        };

        await dbHelpers.createReportRequest(reportRequestData);
        console.log('✅ Report request saved to database');
        onProgress?.(98, 'Saving your information...');
      } catch (dbError) {
        console.warn('⚠️ Failed to save report request to database:', dbError.message);
        // Don't fail the whole process if database save fails
        onProgress?.(98, 'Report ready for download...');
      }

      // Final delay to show completion
      await new Promise(resolve => setTimeout(resolve, 300));
      onProgress?.(100, 'Report generated successfully!');

      return {
        reportContent,
        pdfBlob
      };
    } catch (err) {
      console.error('Report generation error:', err);

      // Provide more specific error messages
      let errorMessage = 'Failed to generate report. ';
      if (err.message.includes('API key')) {
        errorMessage += 'AI service is not configured, but report generation should still work.';
      } else if (err.message.includes('network') || err.message.includes('fetch')) {
        errorMessage += 'Please check your internet connection and try again.';
      } else if (err.message.includes('PDF')) {
        errorMessage += 'There was an issue creating the PDF. Please try again.';
      } else {
        errorMessage += err.message || 'Please try again or contact support if the issue persists.';
      }

      throw new Error(errorMessage);
    }
  };

  const handleDownloadCSV = () => {
    if (!results?.matches) return;

    const headers = ['Rank', 'Country/Region', 'Incentive Name', 'Type', 'Est. Savings', 'Mark'];
    const rows = results.matches.map(match => [
      match.rank,
      match.jurisdiction_name,
      match.incentive_name,
      match.incentive_type,
      `${match.currency || 'USD'} ${match.estimatedSavings?.toLocaleString() || '0'}`,
      `${match.mark_stars}/5`
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `incentive-matches-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'My Film Incentive Matches',
          text: 'Check out my film incentive matches from NuPitch',
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copied to clipboard!');
      }
    } catch (err) {
      toast.error('Failed to share');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Results Not Found</h1>
          <p className="text-gray-600">The results you're looking for could not be found.</p>
        </div>
      </div>
    );
  }

  const sortedMatches = results?.matches ? [...results.matches].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    
    if (sortOrder === 'asc') {
      return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    } else {
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    }
  }) : [];

  return (
    <>
      <Helmet>
        <title>Your Incentive Matches - NuPitch</title>
        <meta name="description" content="View your personalized film tax incentive matches and potential savings." />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container-responsive py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div className="text-center">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Your Incentive Matches
              </h1>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Based on your project details, here are the best-matching tax incentives 
                ranked by potential savings and eligibility.
              </p>
            </div>

            {results && (
              <>
                <ResultsSummary results={results} />
                
                <ActionBar
                  onGenerateReport={() => setShowReportModal(true)}
                  onDownloadCSV={handleDownloadCSV}
                  onShare={handleShare}
                  isShared={isShared}
                />

                <ResultsTable
                  matches={sortedMatches}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                />

                <div className="text-center text-sm text-gray-500 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="font-medium text-yellow-800 mb-1">BETA Notice</p>
                  <p>
                    This is a BETA. Estimates are indicative; always confirm details 
                    directly with the commission.
                  </p>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </div>

      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        onSubmit={handleGenerateReport}
        submissionData={results?.submission}
        incentivesData={results?.matches}
      />
    </>
  );
};

export default Results;